import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';

import { useRevenueMonthly } from '../../hooks/use-dashboard';

const brl = (v: number): string =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const formatMonth = (m: string): string => {
  const [y, mo] = m.split('-');
  return `${mo}/${y?.slice(2)}`;
};

// Hex resolvidos pra Recharts. Match design tokens.
const BRAND = '#7c3aed'; // var(--brand)
const GOOD = '#22c55e'; // var(--good)
const MUTED = '#a1a1aa'; // var(--muted)
const LINE = '#1f1f23'; // var(--line)

/** Story 6.5 — Line chart MRR + Recebido nos últimos 12 meses. */
export function RevenueMonthlyChart() {
  const query = useRevenueMonthly(12);

  if (query.isLoading) {
    return <div className="h-80 animate-pulse rounded-lg border border-line bg-panel-2" />;
  }

  if (query.isError) {
    return (
      <Card title="Receita 12 meses">
        <div className="p-4 text-sm text-bad">Erro ao carregar histórico.</div>
      </Card>
    );
  }

  const points = query.data?.data.points ?? [];

  if (points.length === 0) {
    return (
      <Card title="Receita 12 meses">
        <div className="py-8 text-center text-sm text-muted">
          Sem histórico ainda (aguarda primeiros snapshots — cron 00:30 BRT).
        </div>
      </Card>
    );
  }

  return (
    <Card title="Receita 12 meses (MRR + Recebido)">
      <div className="h-72 w-full p-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 12, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
            <XAxis dataKey="month" stroke={MUTED} fontSize={11} tickFormatter={formatMonth} />
            <YAxis stroke={MUTED} fontSize={11} tickFormatter={(v) => brl(Number(v))} width={70} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: BRAND, strokeOpacity: 0.2 }} />
            <Line
              type="monotone"
              dataKey="mrr"
              stroke={BRAND}
              strokeWidth={2}
              dot={{ fill: BRAND, r: 3 }}
              name="MRR"
            />
            <Line
              type="monotone"
              dataKey="revenue_received"
              stroke={GOOD}
              strokeWidth={2}
              dot={{ fill: GOOD, r: 3 }}
              name="Recebido"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-line bg-panel px-3 py-2 text-xs shadow-md">
      <div className="font-medium text-ink">{formatMonth(String(label))}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="mt-1 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted">{p.name}:</span>
          <span className="font-mono tabular-nums text-ink">{brl(Number(p.value))}</span>
        </div>
      ))}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-panel">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">{title}</h3>
      </div>
      {children}
    </div>
  );
}
